import { createGoldenHeyClient, type GoldenClient } from "@platform/api/golden";
import { useUser } from "@platform/user/client";
import { createContext, type ReactNode, useContext, useState } from "react";

import { localDevHeaders } from "../auth/localDevSession";
import { type SignIn, signIn } from "../auth/signIn";

// The ingress routes /api to the golden service on the app's own host.
const API_BASE_URL = "/api";

const GoldenClientContext = createContext<GoldenClient | null>(null);

export const createGoldenClient = (
  getAccessToken: () => Promise<string | undefined>,
  current: SignIn = signIn,
): GoldenClient =>
  createGoldenHeyClient({
    baseURL: API_BASE_URL,
    // The spec declares bearer auth on every golden route, so the client asks for
    // the session's token, refreshed by the user lib, on each request.
    auth: () => getAccessToken(),
    // DEV first, so a production build drops the local caller entirely.
    headers:
      import.meta.env.DEV && current.kind === "localDev"
        ? localDevHeaders()
        : {},
  });

/** The golden API's client, signed in as the session's user. */
export const GoldenClientProvider = ({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode => {
  const { getAccessToken } = useUser();
  const [client] = useState(() => createGoldenClient(getAccessToken));

  return (
    <GoldenClientContext.Provider value={client}>
      {children}
    </GoldenClientContext.Provider>
  );
};

export const useGoldenClient = (): GoldenClient => {
  const client = useContext(GoldenClientContext);
  if (!client) {
    throw new Error(
      "useGoldenClient must be used within a GoldenClientProvider",
    );
  }
  return client;
};
